// Section Paramètres → Utilisateurs (multi-utilisateurs).
// Visible pour les formules Équipe et Structure. Permet d'inviter, modifier
// le profil, désactiver/réactiver les utilisateurs de l'entreprise.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES_INVITABLES, LIBELLE_ROLE } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, UserPlus, Crown, ArrowUpRight } from "lucide-react";

const VIDE = { email: "", prenom: "", nom: "", role: "" };

export default function UtilisateursSection() {
  const { abonnement } = useAuth();
  const navigate = useNavigate();
  const formule = abonnement?.formule;
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [ouvert, setOuvert] = useState(false);
  const [form, setForm] = useState(VIDE);
  const [envoi, setEnvoi] = useState(false);

  const rolesDispo = ROLES_INVITABLES[formule] || [];

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/utilisateurs");
      setListe(data.utilisateurs || []);
    } catch {
      setListe([]);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (formule && formule !== "solo") charger();
    else setChargement(false);
  }, [formule, charger]);

  // --- Formule Solo : message d'incitation ---
  if (!formule || formule === "solo") {
    return (
      <div data-testid="utilisateurs-solo" className="text-sm text-[#1D3D3D]/80 space-y-3">
        <p>
          La gestion multi-utilisateurs est disponible à partir de la formule{" "}
          <strong>Équipe (6 500 XPF/mois)</strong>.
        </p>
        <Button
          data-testid="bouton-changer-formule"
          onClick={() => navigate("/formules")}
          className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white"
        >
          Changer de formule <ArrowUpRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    );
  }

  const inviter = async () => {
    if (!form.email || !form.prenom || !form.nom || !form.role) {
      toast.error("Merci de renseigner tous les champs.");
      return;
    }
    setEnvoi(true);
    try {
      await api.post("/utilisateurs/inviter", form);
      toast.success("Invitation envoyée par email.");
      setOuvert(false);
      setForm(VIDE);
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Échec de l'invitation.");
    } finally {
      setEnvoi(false);
    }
  };

  const changerRole = async (id, role) => {
    try {
      await api.put(`/utilisateurs/${id}/role`, { role });
      toast.success("Profil mis à jour.");
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Échec de la modification.");
    }
  };

  const basculerStatut = async (u) => {
    const action = u.statut === "inactif" ? "reactiver" : "desactiver";
    try {
      await api.post(`/utilisateurs/${u.id}/${action}`);
      toast.success(action === "reactiver" ? "Utilisateur réactivé." : "Utilisateur désactivé.");
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Action impossible.");
    }
  };

  const badgeStatut = (statut) => {
    const styles = {
      actif: "bg-green-100 text-green-700",
      inactif: "bg-red-100 text-red-700",
      invitation_en_attente: "bg-amber-100 text-amber-700",
    };
    const libelle = { actif: "Actif", inactif: "Inactif", invitation_en_attente: "En attente" };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[statut] || ""}`}>
        {libelle[statut] || statut}
      </span>
    );
  };

  return (
    <div data-testid="utilisateurs-section" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#1D3D3D]/70">
          {formule === "equipe"
            ? "Formule Équipe — jusqu'à 5 utilisateurs."
            : "Formule Structure — utilisateurs illimités."}
        </p>
        <Dialog open={ouvert} onOpenChange={setOuvert}>
          <DialogTrigger asChild>
            <Button data-testid="bouton-inviter-utilisateur" className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
              <UserPlus className="h-4 w-4 mr-2" /> Inviter un utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="dialog-invitation">
            <DialogHeader>
              <DialogTitle>Inviter un utilisateur</DialogTitle>
              <DialogDescription>
                Un email d'activation (valable 72 h) sera envoyé à l'utilisateur.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Prénom</Label>
                <Input data-testid="invitation-prenom" value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
              </div>
              <div>
                <Label>Nom</Label>
                <Input data-testid="invitation-nom" value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="invitation-email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Profil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invitation-role"><SelectValue placeholder="Choisir un profil" /></SelectTrigger>
                  <SelectContent>
                    {rolesDispo.map((r) => (
                      <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{LIBELLE_ROLE[r]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="confirmer-invitation" onClick={inviter} disabled={envoi}
                className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer l'invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {chargement ? (
        <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid="table-utilisateurs" className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#1D3D3D]/60 border-b">
                <th className="py-2 pr-3">Nom / Prénom</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Profil</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((u) => (
                <tr key={u.id} data-testid={`ligne-utilisateur-${u.id}`} className="border-b last:border-0">
                  <td className="py-2.5 pr-3 font-medium text-[#1D3D3D] flex items-center gap-1.5">
                    {u.role === "gerant" && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                    {`${u.prenom || ""} ${u.nom || ""}`.trim() || "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-[#1D3D3D]/70">{u.email}</td>
                  <td className="py-2.5 pr-3">
                    {u.role === "gerant" ? (
                      <span className="font-medium">{u.role_libelle}</span>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changerRole(u.id, v)}>
                        <SelectTrigger data-testid={`select-role-${u.id}`} className="h-8 w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {rolesDispo.map((r) => (
                            <SelectItem key={r} value={r}>{LIBELLE_ROLE[r]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">{badgeStatut(u.statut)}</td>
                  <td className="py-2.5 pr-3 text-right">
                    {u.role !== "gerant" && u.statut !== "invitation_en_attente" && (
                      <Button
                        data-testid={`bouton-statut-${u.id}`}
                        variant="outline" size="sm"
                        onClick={() => basculerStatut(u)}
                        className={u.statut === "inactif" ? "text-green-700" : "text-red-600"}
                      >
                        {u.statut === "inactif" ? "Réactiver" : "Désactiver"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
